'use client';

import { useTranslations } from 'next-intl';
import { Eye, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Link } from '@/i18n/routing';
import { PermissionGate } from '@/features/auth';
import type { Box, BoxStatus } from '../types';

interface PackingCompactRowProps {
  box: Box;
  onDelete: (box: Box) => void;
  statusStyles: Record<BoxStatus, string>;
}

export function PackingCompactRow({
  box,
  onDelete,
  statusStyles
}: PackingCompactRowProps) {
  const t = useTranslations('packing');

  const statusLabels: Record<BoxStatus, string> = {
    available: t('statusAvailable'),
    packing: t('statusPacking'),
    ready_for_shipping: t('statusReadyForShipping'),
    in_shipping: t('statusInShipping'),
    in_transit: t('statusInTransit'),
    received: t('statusReceived')
  };

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      {/* Left: box_number + box_type name */}
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <Link
            href={`/dashboard/packing/${box.id}`}
            className='text-primary truncate font-mono font-medium hover:underline'
          >
            {box.box_number}
          </Link>
          {box.box_type && (
            <span className='text-muted-foreground hidden truncate text-sm sm:inline'>
              {box.box_type.name}
            </span>
          )}
        </div>
      </div>

      {/* Center: capacity (hidden on mobile) */}
      <span className='text-muted-foreground hidden text-sm md:inline'>
        {box.registrations_count ?? 0}/{box.box_type?.max_capacity ?? 0}
      </span>

      {/* Right: status badge */}
      <Badge
        variant='secondary'
        className={cn('shrink-0 text-xs', statusStyles[box.status])}
      >
        {statusLabels[box.status] ?? box.status}
      </Badge>

      {/* Action buttons */}
      <div className='flex shrink-0 items-center gap-1'>
        <Button variant='ghost' size='icon' className='h-7 w-7' asChild>
          <Link href={`/dashboard/packing/${box.id}`}>
            <Eye className='h-3.5 w-3.5' />
          </Link>
        </Button>
        {box.status !== 'received' && (
          <PermissionGate permissions={['boxes.delete']}>
            <Button
              variant='ghost'
              size='icon'
              className='text-destructive h-7 w-7'
              onClick={() => onDelete(box)}
            >
              <Trash2 className='h-3.5 w-3.5' />
            </Button>
          </PermissionGate>
        )}
      </div>
    </div>
  );
}
